import React, { useEffect, useMemo, useState } from "react";
import axiosConfig from "../Service/axiosConfig";
import { Modal } from "react-bootstrap";
import { isDialableMobileNumber } from "../Utils/mobileNumber";
import "./callbackRequests.css";

const profileUrl = (hccId) => `https://indiacastingclub.com/profile/${hccId}`;

const calculateAge = (dob) => {
  if (!dob) return null;
  const dobDate = new Date(dob);
  if (Number.isNaN(dobDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dobDate.getFullYear();
  const monthDiff = today.getMonth() - dobDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getDate())) {
    age--;
  }
  return age;
};

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDuration = (remainingMs) => {
  if (remainingMs <= 0) return "Due now";
  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const CallbackRequestCard = ({ row, now, onCloseRequest }) => {
  const displayName = row?.name || row?.user_name || "--";
  const phone = row?.phone_number || row?.mobile_number;
  const age = calculateAge(row?.dob);
  const endTime = row?.expected_contact_at
    ? new Date(row.expected_contact_at).getTime()
    : 0;
  const remainingMs = endTime - now;
  const remaining = formatDuration(remainingMs);
  const isDue = remainingMs <= 0;
  const isOpen = row?.status === "open";

  return (
    <article className={`callback-card callback-card--${row?.status || "open"}`}>
      <div className="callback-card-header">
        <div className="callback-card-header-left">
          <span className="callback-request-id">#{row?.id}</span>
          <span className={`callback-status-badge ${row?.status || "open"}`}>
            {row?.status || "--"}
          </span>
          {row?.placement ? (
            <span className="callback-placement">{row.placement}</span>
          ) : null}
        </div>
        <div>
          {isOpen ? (
            <span className={`callback-remaining ${isDue ? "due" : "waiting"}`}>
              {isDue ? "Due now" : `Remaining ${remaining}`}
            </span>
          ) : (
            <span className="callback-remaining">Closed {formatDateTime(row?.closed_at)}</span>
          )}
        </div>
      </div>

      <div className="callback-card-body">
        <div className="callback-profile-block">
          {row?.profile_pic ? (
            <img
              className="callback-profile-pic"
              src={row.profile_pic}
              alt={displayName}
            />
          ) : (
            <div className="callback-profile-pic callback-profile-pic--placeholder">?</div>
          )}
          <div className="callback-profile-info">
            <div className="callback-hcc-id">
              {row?.hcc_id || "--"}
              {row?.is_pro ? <span className="callback-pro-badge">PRO</span> : null}
            </div>
            <div className={`callback-name ${row?.is_pro ? "is-pro" : ""}`}>{displayName}</div>
            <div className="callback-contact-line">{row?.email || "--"}</div>
            <div className="callback-contact-line">
              {isDialableMobileNumber(phone) ? (
                <a href={`tel:${phone}`}>{phone}</a>
              ) : (
                phone || "--"
              )}
              {row?.gender || age != null || row?.dob ? (
                <span className="callback-demographics">
                  {" · "}
                  {row?.gender || ""}
                  {age != null ? ` ${age} yrs` : ""}
                  {row?.dob ? ` · ${formatDate(row.dob)}` : ""}
                </span>
              ) : null}
            </div>
            {Array.isArray(row?.crafts_24) && row.crafts_24.length > 0 ? (
              <div className="callback-crafts-list">
                {row.crafts_24.map((craft) => (
                  <span
                    key={craft.id ?? `${craft.role_id}-${craft.role_name}`}
                    className="callback-craft-chip"
                  >
                    {craft.role_name || "--"}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="callback-meta-strip">
          <div className="callback-meta-item">
            <label>Registered</label>
            <span>{formatDate(row?.registered_date)}</span>
          </div>
          <div className="callback-meta-item">
            <label>Images</label>
            <span>{row?.total_model_images ?? "--"}</span>
          </div>
          <div className="callback-meta-item">
            <label>ETA</label>
            <span>{row?.wait_minutes ?? "--"} min</span>
          </div>
          <div className="callback-meta-item">
            <label>Requested</label>
            <span>{formatDateTime(row?.cdate)}</span>
          </div>
          <div className="callback-meta-item">
            <label>Expected</label>
            <span>{formatDateTime(row?.expected_contact_at)}</span>
          </div>
          {!isOpen && row?.closed_at ? (
            <div className="callback-meta-item">
              <label>Closed</label>
              <span>{formatDateTime(row?.closed_at)}</span>
            </div>
          ) : null}
        </div>

        <div className="callback-notes-block">
          <label>Notes</label>
          <span>{row?.admin_notes || "--"}</span>
        </div>

        <div className="callback-card-actions">
          {row?.hcc_id ? (
            <a
              href={profileUrl(row.hcc_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-primary btn-sm"
            >
              Profile
            </a>
          ) : null}
          {isDialableMobileNumber(phone) ? (
            <a href={`tel:${phone}`} className="btn btn-primary btn-sm">
              Call
            </a>
          ) : null}
          {isOpen ? (
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={() => onCloseRequest(row)}
            >
              Close
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
};

const CallbackRequests = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(20);
  const [status, setStatus] = useState("open");
  const [now, setNow] = useState(Date.now());

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailActionLoading, setDetailActionLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailNotes, setDetailNotes] = useState("");

  useEffect(() => {
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const getData = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: rowPerPage,
      };
      if (status) params.status = status;

      const response = await axiosConfig.get("/accounts/callback-requests/admin/", {
        params,
      });
      setRows(response?.data?.results || []);
      setTotalRows(response?.data?.count || 0);
    } catch (error) {
      console.log(error);
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [currentPage, rowPerPage, status]);

  const openDetailModal = (row) => {
    setShowDetailModal(true);
    setSelectedDetail(row || null);
    setDetailNotes(row?.admin_notes || "");
  };

  const closeRequest = async () => {
    if (!selectedDetail?.id) return;
    setDetailActionLoading(true);
    try {
      await axiosConfig.patch(
        `/accounts/callback-requests/admin/${selectedDetail.id}/close/`,
        { admin_notes: detailNotes || "" }
      );
      setShowDetailModal(false);
      setSelectedDetail(null);
      setDetailNotes("");
      getData();
    } catch (error) {
      alert("Failed to close callback request.");
    } finally {
      setDetailActionLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / rowPerPage));
  const rangeStart = totalRows === 0 ? 0 : (currentPage - 1) * rowPerPage + 1;
  const rangeEnd = Math.min(currentPage * rowPerPage, totalRows);

  const metrics = useMemo(() => {
    let open = 0;
    let closed = 0;
    rows.forEach((row) => {
      if (row?.status === "open") open += 1;
      if (row?.status === "closed") closed += 1;
    });
    return { open, closed };
  }, [rows]);

  return (
    <div className="child-sidenav">
      <div className="childnav-content user-screen callback-requests-page">
        <div className="callback-toolbar">
          <div className="btn-group" role="group" aria-label="status tabs">
            <button
              type="button"
              className={`btn btn-sm ${status === "open" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setStatus("open");
                setCurrentPage(1);
              }}
            >
              Open
            </button>
            <button
              type="button"
              className={`btn btn-sm ${status === "closed" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setStatus("closed");
                setCurrentPage(1);
              }}
            >
              Closed
            </button>
          </div>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={getData}>
            Refresh
          </button>
          <div className="callback-toolbar-summary">
            <div>
              <b>{totalRows}</b> total · this page: {metrics.open} open, {metrics.closed} closed
            </div>
          </div>
        </div>

        {loading ? (
          <div className="callback-loader">
            <img
              src={require("../Components/FormParser/images/loader2.gif")}
              alt="Loading..."
            />
          </div>
        ) : rows.length === 0 ? (
          <div className="callback-empty">No callback requests found.</div>
        ) : (
          <div className="callback-list">
            {rows.map((row) => (
              <CallbackRequestCard
                key={row.id}
                row={row}
                now={now}
                onCloseRequest={openDetailModal}
              />
            ))}
          </div>
        )}

        {totalRows > 0 && (
          <div className="callback-pagination">
            <div>
              Rows per page:{" "}
              <select
                value={rowPerPage}
                onChange={(e) => {
                  setRowPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 20, 25, 30, 40, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {rangeStart}-{rangeEnd} of {totalRows}
            </div>
            <div className="callback-pagination-controls">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </button>
              <span>
                Page {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        centered
        size="lg"
        show={showDetailModal}
        onHide={() => {
          if (!detailActionLoading) {
            setShowDetailModal(false);
            setSelectedDetail(null);
            setDetailNotes("");
          }
        }}
      >
        <Modal.Header closeButton={!detailActionLoading}>
          <Modal.Title>Close callback request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDetail && (
            <>
              <div className="mb-3 d-flex gap-3 align-items-start">
                {selectedDetail.profile_pic ? (
                  <img
                    src={selectedDetail.profile_pic}
                    alt=""
                    className="callback-profile-pic"
                  />
                ) : null}
                <div>
                  <div>
                    <b>#{selectedDetail.id}</b> · {selectedDetail.hcc_id || "--"} ·{" "}
                    {selectedDetail.name || selectedDetail.user_name || "--"}
                  </div>
                  <div className="callback-contact-line">
                    {selectedDetail.email || "--"} ·{" "}
                    {selectedDetail.phone_number || selectedDetail.mobile_number || "--"}
                  </div>
                  <div className="callback-contact-line">
                    Placement: {selectedDetail.placement || "--"}
                  </div>
                </div>
              </div>
              <label className="form-label">
                <b>Admin notes</b> (optional)
              </label>
              <textarea
                className="form-control"
                rows={4}
                value={detailNotes}
                disabled={detailActionLoading}
                onChange={(e) => setDetailNotes(e.target.value)}
                placeholder="Connected and resolved."
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={detailActionLoading}
            onClick={() => {
              setShowDetailModal(false);
              setSelectedDetail(null);
              setDetailNotes("");
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={!selectedDetail || detailActionLoading}
            onClick={closeRequest}
          >
            {detailActionLoading ? "Processing..." : "Confirm close"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export { CallbackRequests };
